import React, { ReactNode, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useConnectWallet } from "@web3-onboard/react";
import { useAcreDapp } from "../../providers/AcreDappProvider/AcreDappProvider";
// components
import RoundedButton from "../RoundedButton/RoundedButton";
import RoundedStatusTag from "../RoundedStatusTag/RoundedStatusTag";
import EditListing from "../EditListing/EditListing";
import Modal from "../Modal/Modal";
// utils
import {
  LISTING_TYPE,
  IPropertyNFTDescriptor,
  genUIDForNFT,
} from "../../platformUtils";
import moment from "moment";
import { shortenAddress } from "../../utils/uiUtils";

// media
import verifyFilledStarIcon from "../../media/simple-mono-icons/verify-filled-star-icon.svg";
import clockIconWhite from "../../media/simple-mono-icons/clock-icon-white.svg";

function PropertyWideCard(props: {
  property: IPropertyNFTDescriptor;
  private?: boolean;
}) {
  let _ = require("lodash");
  const { walletAddress } = useAcreDapp();
  const [ownerAddressShort, setOwnerAddressShort] = useState(
    _.truncate(props.property?.listing?.walletAddress)
  );
  const [tenantAddressShort, setTenantAddressShort] = useState(
    _.truncate(props.property?.listing?.rental?.renter)
  );
  const [landlordAddressShort, setLandlordAddressShort] = useState(
    _.truncate(props.property?.listing?.walletAddress)
  );
  const [EditListingModal, setEditListingModal] = useState(<></>);
  let listingStatusTag = <></>;
  const isOwner =
    _.toLower(props.property?.listing?.walletAddress) ===
    _.toLower(walletAddress);
  const isPrivate = props.property?.listing?.is_private;
  let isLandlord = false;
  let isTenant = false;
  let rentalInfo = props.property?.listing?.rental;
  let rentalStartDate = null;
  let rentalEndDate = null;
  //let rentalNextPaymentDate = null;
  let metadataElements = [];
  let nowDate = new Date(); // for consistency

  const handleEditListing = () => {
    setEditListingModal(<Modal openModal={true} children={<EditListing />} />);
  };
  if (rentalInfo?.for_rent) {
    rentalStartDate = new Date(props.property?.listing?.start_date);
    rentalEndDate = moment(
      props.property?.listing?.listing_expiration_date
    ).format("MMM Do YYYY");
    // rentalNextPaymentDate = getNextRentalPeriodEnd(nowDate, rentalInfo);

    if (_.toLower(rentalInfo?.renter) === _.toLower(walletAddress)) {
      isTenant = true;
    } else {
      isLandlord = true;
    }
  }
  if (isTenant) {
    // ANCHOR Tenant metadata
    listingStatusTag = <RoundedStatusTag fill="green">Rented</RoundedStatusTag>;

    metadataElements.push(
      <div className="text-mid-grey text-sm mb-2">
        Lended by {landlordAddressShort} from{" "}
        {rentalStartDate!.toLocaleDateString()} to {rentalEndDate}
      </div>
    );

    metadataElements.push(
      <div className="text-mid-grey text-sm mb-2">
        Next payment due before: 11/10/2022
      </div>
    );

    // TODO: make this actually take to a payment page
    metadataElements.push(<RoundedButton>Make Payment</RoundedButton>);
  } else if (isLandlord && rentalInfo?.is_rented) {
    // ANCHOR Landlord metadata
    listingStatusTag = (
      <RoundedStatusTag fill="green" icon={clockIconWhite} iconLeft={true}>
        Rented Out
      </RoundedStatusTag>
    );

    // TODO: create cool hoverable items for addresses (link to etherscan) and dates (show explanation of what it means & consequences)
    metadataElements.push(
      <div className="text-mid-grey text-sm mb-2">
        Lended to {tenantAddressShort} from{" "}
        {rentalStartDate!.toLocaleDateString()} to {rentalEndDate}
      </div>
    );
  } else {
    // ANCHOR Regular owner metadata

    metadataElements.push(
      <RoundedButton outline={true} grey={true} onClick={handleEditListing}>
        Edit Listing
      </RoundedButton>
    );
    if (props.property?.listing?.auction?.for_auction) {
      listingStatusTag = (
        <RoundedStatusTag fill="caution">For Auction</RoundedStatusTag>
      );
      metadataElements.push(
        <div className="text-mid-grey text-sm mb-2 mt-2 font-bold">
          <span className="text-logo-green font-bold ">Expiration Date: </span>{" "}
          {moment(props.property?.listing?.listing_expiration_date).format(
            "MMM Do YYYY"
          )}
        </div>
      );
    } else if (props.property?.listing?.rental?.for_rent) {
      listingStatusTag = (
        <RoundedStatusTag fill="green">For Rent</RoundedStatusTag>
      );
      metadataElements.push(
        <div className="text-mid-grey text-sm mb-2 mt-2 font-bold">
          <span className="text-logo-green font-bold ">Expiration Date: </span>{" "}
          {moment(props.property?.listing?.listing_expiration_date).format(
            "MMM Do YYYY"
          )}
        </div>
      );
    } else if (props.property?.listing?.fixed_price?.for_fixed_price) {
      listingStatusTag = (
        <RoundedStatusTag fill="light-green">For Sale</RoundedStatusTag>
      );
      metadataElements.push(
        <div className="text-mid-grey text-sm mb-2 mt-2 font-bold">
          <span className="text-logo-green font-bold ">Expiration Date: </span>{" "}
          {moment(props.property?.listing?.listing_expiration_date).format(
            "MMM Do YYYY"
          )}
        </div>
      );
    } else {
      listingStatusTag = (
        <RoundedStatusTag fill="light-green">Not For Sale</RoundedStatusTag>
      );
    }
  }

  return (
    <>
      <div>
        <div
          role="link"
          className="flex h-48 bg-white rounded-2 p-2 shadow-lg transition-all cursor-pointer hover:scale-[102%] hover:shadow-2xl hover:z-10"
        >
          <img
            className={`h-full aspect-6/5 rounded-2 object-cover`}
            src={props.property?.listing?.property?.images?.blockchain}
            alt=""
          />
          <div className="flex-grow p-3 flex flex-col justify-center items-start">
            <div className="text-mid-grey italic text-sm mb-3 inline">
              {props.property?.listing?.metaverse}
              <img
                className="inline ml-1"
                src={verifyFilledStarIcon}
                alt="Verified metaverse property"
              />
              {isPrivate ? (
                <span className=" ml-4 inline-flex items-center rounded-full bg-pink-100 px-2.5 py-0.5 text-xs font-bold text-pink-800">
                  Private
                </span>
              ) : (
                <span className=" ml-4 inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-bold text-purple-800">
                  Public
                </span>
              )}
            </div>
            <div className="font-bold text-2xl mb-2">
              {props.property?.listing?.property?.name}
            </div>
            {metadataElements}
          </div>
          <div className="self-end flex flex-col justify-space-between items-end h-full">
            {listingStatusTag}
          </div>
          {EditListingModal}
        </div>
      </div>
    </>
  );
}

export default PropertyWideCard;
